import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Crown, Star, Award, User, Settings, Plus, Edit2, Save, X, 
  ToggleLeft, ToggleRight, RefreshCw, History, AlertCircle,
  ChevronUp, ChevronDown, Check, GripVertical
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

import { API_URL } from '../utils/apiUrl';

const formatCurrency = (value) => {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${value?.toLocaleString('en-IN') || 0}`;
};

const TIER_ICONS = {
  crown: Crown,
  star: Star,
  award: Award,
  user: User
};

const TierIcon = ({ icon, className }) => {
  const IconComponent = TIER_ICONS[icon] || Star;
  return <IconComponent className={className} />;
};

const TierConfigPage = () => {
  const { token } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    priority: 1,
    aum_threshold: 0,
    brokerage_threshold: 0,
    color: '#C5A059',
    icon: 'star',
    description: ''
  });

  const fetchTiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/tiers?include_inactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiers(res.data.tiers || []);
    } catch (err) {
      console.error('Error fetching tiers:', err);
      // If no tiers exist, initialize them
      if (err.response?.status === 404 || (err.response?.data?.tiers?.length === 0)) {
        await initializeTiers();
      }
    }
  }, [token]);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/tiers/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(res.data);
    } catch (err) {
      console.error('Error fetching overview:', err);
    }
  }, [token]);

  const initializeTiers = async () => {
    try {
      await axios.post(`${API_URL}/tiers/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default tiers initialized');
      fetchTiers();
    } catch (err) {
      console.error('Error initializing tiers:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTiers(), fetchOverview()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTiers, fetchOverview]);

  const handleCreateTier = async () => {
    try {
      await axios.post(`${API_URL}/tiers`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tier created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchTiers();
      fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tier');
    }
  };

  const handleUpdateTier = async (tierId) => {
    try {
      const updates = {
        name: formData.name,
        priority: formData.priority,
        aum_threshold: formData.aum_threshold,
        brokerage_threshold: formData.brokerage_threshold,
        color: formData.color,
        icon: formData.icon,
        description: formData.description
      };
      
      await axios.put(`${API_URL}/tiers/${tierId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tier updated successfully');
      setEditingTier(null);
      resetForm();
      fetchTiers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tier');
    }
  };

  const handleToggleTier = async (tierId, currentStatus) => {
    try {
      await axios.post(`${API_URL}/tiers/${tierId}/toggle?is_active=${!currentStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Tier ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchTiers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle tier');
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await axios.post(`${API_URL}/tiers/recalculate?reason=manual_admin_trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const res = await axios.get(`${API_URL}/tiers/audit-log?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(res.data.audit_logs || []);
      setShowAuditLog(true);
    } catch (err) {
      toast.error('Failed to fetch audit log');
    }
  };

  const startEditing = (tier) => {
    setEditingTier(tier.id);
    setFormData({
      name: tier.name,
      code: tier.code,
      priority: tier.priority,
      aum_threshold: tier.aum_threshold,
      brokerage_threshold: tier.brokerage_threshold,
      color: tier.color,
      icon: tier.icon,
      description: tier.description || ''
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      priority: 1,
      aum_threshold: 0,
      brokerage_threshold: 0,
      color: '#C5A059',
      icon: 'star',
      description: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Settings className="w-7 h-7 text-[#C5A059]" />
              Client Tier Configuration
            </h1>
            <p className="text-slate-400 mt-1">Manage client tiering rules and thresholds</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={fetchAuditLog}
              className="border-white/10"
            >
              <History className="w-4 h-4 mr-2" />
              Audit Log
            </Button>
            <Button 
              onClick={handleRecalculate}
              disabled={recalculating}
              className="bg-[#C5A059] hover:bg-[#B8934F] text-black"
            >
              {recalculating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Recalculate All Tiers
            </Button>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tier
            </Button>
          </div>
        </div>

        {/* Overview Stats */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {overview.tier_distribution?.map((tier) => (
              <Card key={tier.tier_code} className="card-vault">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">{tier.tier_name}</p>
                      <p className="text-2xl font-bold text-white">{tier.client_count}</p>
                      <p className="text-xs text-slate-500">{tier.percentage}% of clients</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#C5A059] font-medium">{formatCurrency(tier.total_aum)}</p>
                      <p className="text-xs text-slate-500">Total AUM</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tier Configuration Cards */}
        <Card className="card-vault">
          <CardHeader className="border-b border-white/5">
            <CardTitle className="font-heading text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-[#C5A059]" />
              Tier Definitions
              <span className="text-xs text-slate-400 ml-2">(Ordered by priority: highest first)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {tiers.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 mx-auto text-slate-500 mb-3" />
                <p className="text-slate-400">No tiers configured</p>
                <Button 
                  onClick={initializeTiers}
                  className="mt-4 bg-[#C5A059] hover:bg-[#B8934F] text-black"
                >
                  Initialize Default Tiers
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {tiers.map((tier, index) => (
                  <div 
                    key={tier.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      tier.is_active 
                        ? 'bg-white/5 border-white/10 hover:border-[#C5A059]/30' 
                        : 'bg-slate-800/50 border-slate-700/50 opacity-60'
                    }`}
                  >
                    {editingTier === tier.id ? (
                      /* Edit Mode */
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Tier Name</label>
                            <Input
                              value={formData.name}
                              onChange={(e) => setFormData({...formData, name: e.target.value})}
                              className="bg-white/5 border-white/10"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Priority</label>
                            <Input
                              type="number"
                              value={formData.priority}
                              onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                              className="bg-white/5 border-white/10"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">AUM Threshold (₹)</label>
                            <Input
                              type="number"
                              value={formData.aum_threshold}
                              onChange={(e) => setFormData({...formData, aum_threshold: parseFloat(e.target.value)})}
                              className="bg-white/5 border-white/10"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Brokerage Threshold (₹)</label>
                            <Input
                              type="number"
                              value={formData.brokerage_threshold}
                              onChange={(e) => setFormData({...formData, brokerage_threshold: parseFloat(e.target.value)})}
                              className="bg-white/5 border-white/10"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Color</label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={formData.color}
                                onChange={(e) => setFormData({...formData, color: e.target.value})}
                                className="w-12 h-9 p-1 bg-white/5 border-white/10"
                              />
                              <Input
                                value={formData.color}
                                onChange={(e) => setFormData({...formData, color: e.target.value})}
                                className="bg-white/5 border-white/10 flex-1"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 block mb-1">Icon</label>
                            <select
                              value={formData.icon}
                              onChange={(e) => setFormData({...formData, icon: e.target.value})}
                              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white"
                            >
                              <option value="crown">Crown</option>
                              <option value="star">Star</option>
                              <option value="award">Award</option>
                              <option value="user">User</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" size="sm" onClick={() => { setEditingTier(null); resetForm(); }}>
                            <X className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                          <Button size="sm" onClick={() => handleUpdateTier(tier.id)} className="bg-[#C5A059] hover:bg-[#B8934F] text-black">
                            <Save className="w-4 h-4 mr-1" /> Save Changes
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2 text-slate-500">
                            <GripVertical className="w-4 h-4" />
                            <span className="text-xs font-mono">#{tier.priority}</span>
                          </div>
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${tier.color}20` }}
                          >
                            <TierIcon icon={tier.icon} className="w-5 h-5" style={{ color: tier.color }} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{tier.name}</span>
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300 font-mono">
                                {tier.code}
                              </span>
                              {tier.is_default && (
                                <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                                  Default
                                </span>
                              )}
                              {!tier.is_active && (
                                <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-400 mt-0.5">{tier.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6">
                          {/* Thresholds */}
                          <div className="text-right">
                            <div className="text-sm">
                              <span className="text-slate-400">AUM: </span>
                              <span className="text-white font-medium">{formatCurrency(tier.aum_threshold)}</span>
                            </div>
                            <div className="text-sm">
                              <span className="text-slate-400">Brokerage: </span>
                              <span className="text-white font-medium">{formatCurrency(tier.brokerage_threshold)}</span>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => startEditing(tier)}
                              className="text-slate-400 hover:text-white"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {!tier.is_default && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleToggleTier(tier.id, tier.is_active)}
                                className={tier.is_active ? 'text-emerald-400' : 'text-slate-500'}
                              >
                                {tier.is_active ? (
                                  <ToggleRight className="w-5 h-5" />
                                ) : (
                                  <ToggleLeft className="w-5 h-5" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recalculation Info */}
        {overview?.config && (
          <Card className="card-vault">
            <CardContent className="p-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <span className="text-slate-400">Recalculation Frequency:</span>
                  <span className="text-white capitalize">{overview.config.recalculation_frequency}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-slate-400">Last Recalculation:</span>
                  <span className="text-white">
                    {overview.config.last_recalculation 
                      ? new Date(overview.config.last_recalculation).toLocaleString() 
                      : 'Never'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Tier Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#C5A059]" />
              Create New Tier
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Define a new client tier with qualification thresholds.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Tier Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Diamond Client"
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Tier Code *</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toLowerCase()})}
                  placeholder="e.g., diamond"
                  className="bg-white/5 border-white/10"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Priority (Higher = Better)</label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Icon</label>
                <select
                  value={formData.icon}
                  onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white"
                >
                  <option value="crown">Crown</option>
                  <option value="star">Star</option>
                  <option value="award">Award</option>
                  <option value="user">User</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">AUM Threshold (₹)</label>
                <Input
                  type="number"
                  value={formData.aum_threshold}
                  onChange={(e) => setFormData({...formData, aum_threshold: parseFloat(e.target.value) || 0})}
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Brokerage Threshold (₹)</label>
                <Input
                  type="number"
                  value={formData.brokerage_threshold}
                  onChange={(e) => setFormData({...formData, brokerage_threshold: parseFloat(e.target.value) || 0})}
                  className="bg-white/5 border-white/10"
                />
              </div>
            </div>
            
            <div>
              <label className="text-xs text-slate-400 block mb-1">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optional description"
                className="bg-white/5 border-white/10"
              />
            </div>
            
            <div className="p-3 bg-slate-800 rounded-lg text-xs text-slate-400">
              <strong className="text-white">Note:</strong> Client qualifies for this tier if EITHER AUM OR Brokerage threshold is met.
              If multiple tiers qualify, the highest priority tier is assigned.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }} className="border-white/10">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTier}
              disabled={!formData.name || !formData.code}
              className="bg-[#C5A059] hover:bg-[#B8934F] text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Tier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={showAuditLog} onOpenChange={setShowAuditLog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-[#C5A059]" />
              Tier Configuration Audit Log
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2 py-4">
            {auditLogs.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No audit logs found</p>
            ) : (
              auditLogs.map((log, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      log.action === 'CREATE' ? 'bg-emerald-500/20 text-emerald-400' :
                      log.action === 'UPDATE' ? 'bg-blue-500/20 text-blue-400' :
                      log.action === 'DEACTIVATE' ? 'bg-red-500/20 text-red-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {log.action}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300">
                    {log.user_email || 'System'}
                  </p>
                  {log.changes && (
                    <pre className="text-xs text-slate-500 mt-2 overflow-x-auto">
                      {JSON.stringify(log.changes, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditLog(false)} className="border-white/10">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TierConfigPage;
